import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, FileText, QrCode, RefreshCw, ShieldX } from "lucide-react";

import { StatusPill } from "@/components/Progress";
import { api } from "@/lib/api-client";
import { WARRANTY_LABEL, useLocale } from "@/lib/locale";
import type { Certificate, CertificateHistoryEntry } from "@/lib/domain";

export function CertificatePanel({
  surfaceId,
  qrToken,
  handoverApproved,
  isAdmin,
  onSurfaceChanged,
}: {
  surfaceId: string;
  qrToken: string;
  handoverApproved: boolean;
  isAdmin: boolean;
  onSurfaceChanged?: () => void;
}) {
  const { t, locale } = useLocale();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [history, setHistory] = useState<CertificateHistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { certificate: cert, history: hist } = await api.certificate(surfaceId);
      setCertificate(cert);
      setHistory(hist);
    } catch {
      setCertificate(null);
    }
  }, [surfaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      onSurfaceChanged?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Operazione non riuscita";
      try {
        const parsed = JSON.parse(message) as { message?: string };
        setError(parsed.message ?? message);
      } catch {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  }

  const verifyPath = `/verify/${qrToken}`;

  return (
    <section className="glass-panel-strong mt-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
          {t.certificate}
        </h2>
        {certificate && (
          <StatusPill
            status={certificate.status}
            label={
              certificate.status === "valid"
                ? locale === "it" ? "Valido" : "Valid"
                : certificate.status === "revoked"
                  ? locale === "it" ? "Revocato" : "Revoked"
                  : locale === "it" ? "Sostituito" : "Superseded"
            }
          />
        )}
      </div>

      {!certificate && (
        <div className="mt-3">
          <p className="text-[0.8rem] text-ice/70">
            {handoverApproved
              ? locale === "it"
                ? "Il collaudo è approvato: il certificato può essere emesso."
                : "Inspection approved — the certificate can be issued."
              : locale === "it"
                ? "Il certificato si emette dopo l'approvazione della fase «Collaudo e consegna»."
                : "The certificate can be issued once the final stage is approved."}
          </p>

          {isAdmin ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                run(() => api.issueCertificate(surfaceId, { locale }))
              }
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-magenta px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper transition hover:brightness-110 disabled:opacity-60"
            >
              <BadgeCheck className="h-3.5 w-3.5" />
              {busy
                ? "…"
                : locale === "it"
                  ? "Emetti certificato"
                  : "Issue certificate"}
            </button>
          ) : (
            <p className="mt-2 text-[0.68rem] text-ice/45">{t.notAvailable}</p>
          )}
        </div>
      )}

      {certificate && (
        <div className="mt-3">
          <div className="num font-serif text-2xl text-pink">
            {certificate.number}
          </div>
          <div className="mt-2 grid gap-1.5 text-[0.72rem] text-ice/70 sm:grid-cols-2">
            <span>
              {locale === "it" ? "Revisione" : "Revision"}:{" "}
              <b className="text-paper">
                {String(certificate.version).padStart(2, "0")}
              </b>
            </span>
            <span>
              {locale === "it" ? "Emesso" : "Issued"}:{" "}
              <b className="num text-paper">
                {certificate.issuedAt.slice(0, 10)}
              </b>
            </span>
          </div>

          {certificate.revokedReason && (
            <p className="mt-2 text-[0.72rem] text-pink">
              {certificate.revokedReason}
            </p>
          )}

          {certificate.warrantyItems && certificate.warrantyItems.length > 0 && (
            <div className="mt-3 grid gap-1">
              {certificate.warrantyItems.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between border-b border-white/10 pb-1 text-[0.72rem] last:border-0"
                >
                  <span className="text-ice/70">
                    {WARRANTY_LABEL[locale][w.kind] ?? w.kind}
                  </span>
                  <span className="num text-ice/60">
                    {w.years} {locale === "it" ? "anni" : "yrs"} ·{" "}
                    {w.expiresAt.slice(0, 10)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <a
              href={api.certificatePdfUrl(certificate.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-ice/80 transition hover:text-paper"
            >
              <FileText className="h-3 w-3" />
              PDF
            </a>
            <a
              href={verifyPath}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-ice/80 transition hover:text-paper"
            >
              <QrCode className="h-3 w-3" />
              {locale === "it" ? "Pagina pubblica" : "Public page"}
            </a>

            {isAdmin && certificate.status !== "revoked" && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    run(() => api.reissueCertificate(surfaceId, { locale }))
                  }
                  className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-goldbright transition hover:bg-gold/20 disabled:opacity-50"
                >
                  <RefreshCw className="h-3 w-3" />
                  {locale === "it" ? "Nuova revisione" : "Re-issue"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt(
                      locale === "it"
                        ? "Motivo della revoca"
                        : "Reason for revocation",
                    );
                    if (reason && reason.length >= 3) {
                      void run(() =>
                        api.revokeCertificate(certificate.id, reason),
                      );
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-rose/60 bg-rose/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-pink transition hover:bg-rose/25 disabled:opacity-50"
                >
                  <ShieldX className="h-3 w-3" />
                  {locale === "it" ? "Revoca" : "Revoke"}
                </button>
              </>
            )}
          </div>

          {history.length > 1 && (
            <div className="mt-4 text-[0.66rem] text-ice/50">
              {locale === "it" ? "Revisioni" : "Revisions"}:{" "}
              {history
                .map(
                  (h) =>
                    `v${h.version} ${h.status === "superseded" ? "↺" : "•"}`,
                )
                .join("  ")}
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-3 text-[0.72rem] text-pink">{error}</p>}
    </section>
  );
}
